import React from 'react';
import FormInput from '../components/FormInput';
import { Button } from '../components/Button';
import { SizeEnum } from '../utils';
import TrackingFooter from '../components/TrackingFooter';
import BlueBox from '../assets/images/blue-box.jpg';
import { ReactComponent as ShipAidMark } from '../assets/icons/ShipAid.svg';
import { configToCssVars } from './theme';
import { TrackingPageConfig } from './types';

/**
 * The shopper-facing "track your order" search screen.
 *
 * Props-only like `TrackingPageView`, so the merchant editor can preview it
 * against fixture state rather than keeping a second copy of the design.
 */

export type SearchScreenViewProps = {
  config?: TrackingPageConfig;
  /** Pre-config cover column, used when the config sets no cover image. */
  legacyCoverImageUrl?: string | null;
  /** Present when the shopper is on a merchant's branded subdomain. */
  customDomain?: string | null;
  emailOrPhone: string;
  trackingOrOrderNumber: string;
  onEmailOrPhoneChange: (value: string) => void;
  onTrackingOrOrderNumberChange: (value: string) => void;
  onSubmit: () => void;
  error?: string | null;
};

export function SearchScreenView({
  config,
  legacyCoverImageUrl = null,
  customDomain = null,
  emailOrPhone,
  trackingOrOrderNumber,
  onEmailOrPhoneChange,
  onTrackingOrOrderNumberChange,
  onSubmit,
  error = null,
}: SearchScreenViewProps) {
  const coverImage =
    config?.branding?.coverImageUrl ?? legacyCoverImageUrl ?? BlueBox;
  const merchantLogo = config?.branding?.logoUrl ?? null;
  const coverOnRight = config?.branding?.coverImagePosition === 'right';

  return (
    <div
      data-testid="search-screen-root"
      style={{
        ...configToCssVars(config),
        background: 'var(--tp-bg)',
        color: 'var(--tp-text)',
        fontFamily: 'var(--tp-font)',
      }}
      className="flex flex-col min-h-screen justify-between"
    >
      <div className="h-full max-w-6xl mx-auto w-full form-section">
        <header className="px-8 pt-8">
          {merchantLogo ? (
            <img
              data-testid="search-logo"
              src={merchantLogo}
              alt={customDomain ?? 'Store'}
              style={{ height: `${config?.branding?.logoHeight ?? 48}px` }}
              className="w-auto object-contain"
            />
          ) : (
            <span
              data-testid="search-logo-fallback"
              className="inline-flex items-center gap-2"
              aria-label="ShipAid"
            >
              <ShipAidMark
                className="w-10"
                style={{ color: 'var(--tp-primary)' }}
              />
              <span className="font-bold text-xl tracking-tight">SHIPAID</span>
            </span>
          )}
        </header>

        <div className="my-10">
          <div
            data-testid="search-layout"
            data-cover={coverOnRight ? 'right' : 'left'}
            className={`block md:grid gap-8 items-center justify-between grid-cols-2${
              coverOnRight ? ' md:[direction:rtl]' : ''
            }`}
          >
            <div className="mx-auto pt-8 px-8 md:px-0 md:[direction:ltr]">
              <img
                data-testid="search-cover"
                className="w-full mx-auto"
                style={{ borderRadius: 'var(--tp-radius)' }}
                src={coverImage}
                alt=""
              />
            </div>

            <div className="span-1 px-8 my-12 md:my-20 md:[direction:ltr]">
              <h1 className="text-3xl font-bold mb-4">Track your order</h1>
              {!customDomain ? (
                <p className="font-light text-[14px] md:text-base py-4 opacity-80">
                  Check the status of your order and track every step from your
                  favorite brand to your door. Simply enter your order number
                  and email or tracking code and we will get you the latest
                  update!
                </p>
              ) : null}

              <form
                className="my-8"
                onSubmit={(event) => {
                  event.preventDefault();
                  onSubmit();
                }}
              >
                <h2 className="font-light mb-4">
                  Please enter your Email and Tracking Number or Order ID below
                  to check the status of your order
                </h2>
                <div className="mb-4">
                  <FormInput
                    id="emailOrPhone"
                    name="emailOrPhone"
                    label=""
                    value={emailOrPhone}
                    onChange={(e) => onEmailOrPhoneChange(e.target.value)}
                    placeholder="Email Or Phone"
                    type="text"
                  />
                </div>
                <div className="mb-4">
                  <FormInput
                    id="trackingOrOrderNumber"
                    name="trackingOrOrderNumber"
                    label=""
                    value={trackingOrOrderNumber}
                    onChange={(e) =>
                      onTrackingOrOrderNumberChange(e.target.value)
                    }
                    placeholder="Tracking Number Or Order Number (Required)"
                    type="text"
                  />
                </div>
                <Button
                  size={SizeEnum.xl}
                  isRounded
                  type="submit"
                  disabled={trackingOrOrderNumber === ''}
                  className="text-white w-full rounded-full flex justify-center items-center text-center mt-4 hover:bg-opacity-80"
                  onClick={onSubmit}
                >
                  Track
                </Button>
                {error ? (
                  <div
                    role="alert"
                    className="text-md text-red-500 text-center m-2"
                  >
                    {error}
                  </div>
                ) : null}
              </form>
            </div>
          </div>
        </div>
      </div>
      <TrackingFooter isHomePage={true} />
    </div>
  );
}
